import React, { useState, useEffect, useRef, memo } from 'react';
import { Brain, Search, Target, TrendingUp, CheckCircle, Zap } from 'lucide-react';

const Loading = memo(({ totalMs = 25000, holdAt = 98, checkScrapingStatus, onComplete }) => {
  // Real backend duration is ~240s for the OpenAI audit call. Keep the timer
  // at 280s so the progress ring fills slightly slower than the AI typically
  // returns, then cap at 95% with an "almost done" message if the AI is
  // taking longer than expected. Loading unmounts when App.js transitions
  // to the dashboard, so the cap is purely defensive.
  const TIMER_DURATION_S = 280;
  const PROGRESS_CAP = 95;

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scrapingComplete, setScrapingComplete] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(TIMER_DURATION_S);
  const [pastExpected, setPastExpected] = useState(false);
  const startRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const timerStartRef = useRef(null);

  const analysisSteps = [
    { icon: <Search className="w-4 h-4" />, title: "Analyzing Profile Structure", description: "Evaluating headline and summary" },
    { icon: <Target className="w-4 h-4" />, title: "Assessing Positioning", description: "Reviewing leadership language" },
    { icon: <TrendingUp className="w-4 h-4" />, title: "Measuring Impact", description: "Analyzing achievements" },
    { icon: <Brain className="w-4 h-4" />, title: "Generating Recommendations", description: "Creating action plans" }
  ];

  useEffect(() => {
    if (checkScrapingStatus) {
      checkIntervalRef.current = setInterval(() => {
        const status = checkScrapingStatus();
        if (status.isComplete && !scrapingComplete) {
          setScrapingComplete(true);
          if (onComplete && !startRef.completionTriggered) {
            startRef.completionTriggered = true;
            setTimeout(() => onComplete(), 500);
          }
        }
      }, 2000);
    }
    return () => { if (checkIntervalRef.current) clearInterval(checkIntervalRef.current); };
  }, [checkScrapingStatus, scrapingComplete, onComplete]);

  useEffect(() => {
    if (!timerStartRef.current) timerStartRef.current = Date.now();
    const timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
      const remaining = Math.max(TIMER_DURATION_S - elapsed, 0);
      setTimeRemaining(remaining);
      // Cap visible progress at 95% so we never lie about "100% completed."
      // The dashboard handoff (in App.js) is what ends this screen.
      const progressPercent = Math.min((elapsed / TIMER_DURATION_S) * 100, PROGRESS_CAP);
      setProgress(progressPercent);
      if (elapsed >= TIMER_DURATION_S && !pastExpected) setPastExpected(true);
      const stepIndex = Math.min(Math.floor((progressPercent / 100) * analysisSteps.length), analysisSteps.length - 1);
      if (stepIndex !== currentStep) setCurrentStep(stepIndex);
    }, 1000);
    return () => clearInterval(timerInterval);
  }, [currentStep, analysisSteps.length, pastExpected]);

  return (
    <section style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f5f3ff 100%)' }}>
      <div style={{ textAlign: 'center', maxWidth: '500px', width: '100%', padding: '1rem' }}>
        
        {/* Loading Icon */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '80px', height: '80px',
          background: 'linear-gradient(135deg, #0077B5 0%, #6366f1 100%)',
          borderRadius: '50%', marginBottom: '1rem', position: 'relative',
          boxShadow: '0 12px 30px rgba(0, 119, 181, 0.3)',
          animation: 'glow 3s ease-in-out infinite'
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.3), transparent)', borderRadius: '50%', animation: 'spin 3s linear infinite' }} />
          <Zap style={{ color: 'white', width: '32px', height: '32px', zIndex: 1 }} />
        </div>

        <h2 style={{ fontSize: '1.4rem', fontWeight: '600', background: 'linear-gradient(135deg, #0077B5 0%, #004182 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.25rem' }}>
          Analyzing your LinkedIn Profile
        </h2>
        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
          Our AI is building a personalized report
        </p>

        {/* Progress Ring */}
        <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 0.75rem' }}>
          <svg style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }} width="120" height="120">
            <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(0, 119, 181, 0.1)" strokeWidth="8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="url(#timerGradient)" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 52}`} strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
              style={{ transition: 'stroke-dashoffset 1s linear' }} />
            <defs>
              <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0077B5" /><stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
          <div style={{ position: 'absolute', inset: '10px', background: 'white', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 25px rgba(0, 119, 181, 0.15)' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', background: 'linear-gradient(135deg, #0077B5 0%, #8b5cf6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{Math.round(progress)}%</div>
            <div style={{ fontSize: '0.6rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{pastExpected ? 'Finalizing' : 'Completed'}</div>
          </div>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: '1rem' }}>
          {pastExpected
            ? 'Almost done. Generating high-quality recommendations.'
            : `Est. time • ${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`}
        </p>

        {/* Analysis Steps */}
        <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '12px', padding: '1rem', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem' }}>Analysis in Progress</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {analysisSteps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              return (
                <div key={index} style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  background: isCurrent ? 'rgba(0, 119, 181, 0.08)' : isCompleted ? 'rgba(16, 185, 129, 0.08)' : 'rgba(248, 250, 252, 0.8)',
                  border: `1px solid ${isCurrent ? 'rgba(0, 119, 181, 0.3)' : isCompleted ? 'rgba(16, 185, 129, 0.3)' : 'rgba(226, 232, 240, 0.5)'}`,
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isCompleted ? 'linear-gradient(135deg, #10b981, #059669)' : isCurrent ? 'linear-gradient(135deg, #0077B5, #6366f1)' : '#94a3b8',
                    color: 'white', flexShrink: 0
                  }}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : step.icon}
                  </div>
                  <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: isCompleted ? '#059669' : isCurrent ? '#0077B5' : '#64748b' }}>{step.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.description}</div>
                  </div>
                  {isCurrent && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'linear-gradient(135deg, #0077B5, #6366f1)', animation: 'pulse 2s infinite', flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '0.6rem', padding: '0.5rem', background: 'rgba(248, 250, 252, 0.8)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic', margin: 0 }}>
              💡 Optimized profiles get 5x more connections.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes glow { 0%, 100% { box-shadow: 0 12px 30px rgba(0,119,181,0.3); } 50% { box-shadow: 0 16px 40px rgba(0,119,181,0.45); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.1); opacity: 1; } }
      `}</style>
    </section>
  );
});

Loading.displayName = 'Loading';

export default Loading;
